'use client'

import { useRef, useState } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { Check } from 'lucide-react'

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
  Switch,
} from '@mdxui/primitives'

import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'

interface PricingPlan {
  name: string
  monthlyPrice: string
  /** Yearly price — only used when billing is 'recurring'. */
  yearlyPrice?: string
  period?: {
    monthly: string
    yearly: string
  }
  /** Sub-label under the price in 'one-time' billing (e.g. "one-time", "per project"). Omit for no label. */
  priceSuffix?: string
  description?: string
  buttonText: string
  buttonUrl?: string
  buttonVariant?: 'default' | 'outline'
  highlighted?: boolean
  features: string[]
}

const defaultPlans: PricingPlan[] = [
  {
    name: 'Starter',
    monthlyPrice: '$10',
    yearlyPrice: '$100',
    period: { monthly: 'per month', yearly: 'per year' },
    description: 'Perfect for individuals getting started.',
    buttonText: 'Get Started',
    buttonVariant: 'outline',
    highlighted: false,
    features: ['Up to 5 projects', 'Basic analytics', 'Community support', '1GB storage'],
  },
  {
    name: 'Pro',
    monthlyPrice: '$25',
    yearlyPrice: '$250',
    period: { monthly: 'per month', yearly: 'per year' },
    description: 'For growing teams with more needs.',
    buttonText: 'Get Started',
    buttonVariant: 'default',
    highlighted: true,
    features: ['Unlimited projects', 'Advanced analytics', 'Priority support', '10GB storage'],
  },
  {
    name: 'Premium',
    monthlyPrice: '$50',
    yearlyPrice: '$500',
    period: { monthly: 'per month', yearly: 'per year' },
    description: 'For large teams with advanced needs.',
    buttonText: 'Get Started',
    buttonVariant: 'outline',
    highlighted: false,
    features: ['Everything in Pro', 'Dedicated support', 'Custom integrations', 'Unlimited storage'],
  },
]

/**
 * Props for PricingPlansBordered component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Three pricing tiers with toggle for monthly/yearly
 * - Cards with rounded corners and feature lists
 * - Middle tier can be highlighted with border
 */
export interface PricingPlansBorderedProps {
  /** Main heading text */
  heading?: string
  /** Supporting description shown centered under the heading */
  description?: string
  /** Pricing plans configuration */
  plans?: PricingPlan[]
  /**
   * Billing model.
   * - `'recurring'` (default): Monthly/Yearly toggle, prices use each plan's `period` labels.
   * - `'one-time'`: no toggle, a single price (`monthlyPrice`) with optional `priceSuffix`.
   *   Use for pay-once / one-time-outcome services where monthly/yearly makes no sense.
   * @default 'recurring'
   */
  billing?: 'recurring' | 'one-time'
  /** Additional CSS classes */
  className?: string
}

/**
 * PricingPlansBordered - Three tier pricing with toggle
 *
 * **Layout:**
 * ```
 * ┌────────────────────────────────────────┐
 * │         Simple Pricing Plans           │
 * │         [Monthly] [Yearly]             │
 * │  ┌──────┐  ┌──────────┐  ┌──────┐     │
 * │  │Basic │  │ Standard │  │Premium│     │
 * │  │ $0   │  │   $20    │  │Custom │     │
 * │  │      │  │(highlight)│  │      │     │
 * │  └──────┘  └──────────┘  └──────┘     │
 * └────────────────────────────────────────┘
 * ```
 */
const PricingPlansBordered = ({
  heading = 'Simple Pricing Plans',
  description,
  plans = defaultPlans,
  billing = 'recurring',
  className,
}: PricingPlansBorderedProps) => {
  const [isYearly, setIsYearly] = useState(false)
  const recurring = billing === 'recurring'
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('bg-background py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container mx-auto max-w-7xl flex flex-col gap-10 lg:gap-12">
        <motion.div className="flex flex-col items-center gap-4 text-center" variants={headerAnimation}>
          <h2 className="text-3xl font-semibold tracking-tight lg:text-4xl">{heading}</h2>
          {description && <Markdown size="base">{description}</Markdown>}
        </motion.div>

        {recurring && (
          <motion.div className="flex items-center justify-center gap-3 text-sm" variants={headerAnimation}>
            Monthly
            <Switch checked={isYearly} onCheckedChange={() => setIsYearly(!isYearly)} />
            Yearly
          </motion.div>
        )}

        <motion.div
          className="flex w-full flex-col items-stretch justify-center gap-6 md:flex-row"
          variants={gridContainerVariants(0.1, 0.2)}
        >
          {plans.map((plan) => (
            <motion.div key={plan.name} className="w-full md:flex-1 md:max-w-sm" variants={itemAnimation}>
              <Card
                className={cn(
                  'flex h-full flex-col rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border shadow-sm',
                  plan.highlighted ? 'border-2 border-primary' : 'border-border'
                )}
              >
                <CardHeader>
                  <CardTitle className="text-lg font-semibold">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-medium lg:text-5xl">
                        {recurring && isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                      </span>
                      {(() => {
                        const suffix = recurring
                          ? isYearly
                            ? plan.period?.yearly
                            : plan.period?.monthly
                          : plan.priceSuffix
                        if (!suffix) return null
                        return (
                          <span className="ml-2 text-base text-muted-foreground">
                            {suffix}
                          </span>
                        )
                      })()}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex flex-1 flex-col px-6 pt-6">
                  {/* Description - fixed height for consistency */}
                  <div className="min-h-[40px]">
                    {plan.description && (
                      <p className="text-sm text-muted-foreground">
                        {plan.description}
                      </p>
                    )}
                  </div>

                  <Button
                    className="mt-6 w-full"
                    variant={plan.buttonVariant || 'default'}
                    asChild
                  >
                    <a href={plan.buttonUrl || '#'}>{plan.buttonText}</a>
                  </Button>

                  <div className="relative mb-4 mt-8 flex items-center justify-center overflow-hidden">
                    <Separator className="flex-1" />
                    <span className="px-3 text-xs text-muted-foreground opacity-50">FEATURES</span>
                    <Separator className="flex-1" />
                  </div>

                  <ul className="mt-4 space-y-4">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center">
                        <Check className="size-5 shrink-0 text-primary" />
                        <span className="ml-3 text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  )
}

export { PricingPlansBordered }
export type { PricingPlan as PricingPlansBorderedPlan }
